// src/components/JobList.js

import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import JobItem from "./JobItem";
import Select from "react-select";
import JobBarChart from "./JobBarChart";
import RadarChart from "./RadarChart";
import ProfileSidebar from "./ProfileSidebar"; // Import the ProfileSidebar component
import { darkModeStyles } from "./JobListStyles";
import "./JobList.css";
import {
  FaBars,
  FaTimes,
  FaArrowRight,
  FaChevronDown,
  FaHistory,
  FaTrashAlt,
} from "react-icons/fa"; // Import Chevron Icons
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { AUTH_BYPASS, DEV_MODE, E2E_AUTH_MODE } from "../config";
import { PropagateLoader } from "react-spinners"; // Import PropagateLoader
import { useAuth0 } from "@auth0/auth0-react";

const getDevUser = () => ({
  id: "dev_user_id",
  user_metadata: {
    full_name: "Dev User",
  },
  email: "devuser@example.com",
});

const E2E_AUTH_FLAG = "jobr_e2e_force_auth";
const PENDING_SIGNUP_AUTO_GENERATE_FLAG = "jobr_pending_signup_auto_generate";
const PENDING_EXISTING_DREAM_SUGGESTION_KEY = "jobr_pending_existing_dream_suggestion";
// Digest emails deep-link to a single job via ?id=<jobId>. The id is parked
// here while the visitor signs in because the auth redirect returns to the
// site root without the query string.
const PENDING_JOB_ID_KEY = "jobr_pending_job_id";
// Same idea for the "?settings=email-frequency" deep link from the digest
// footer: the auth redirect drops the query string, so park the value while
// the visitor signs in and re-attach it when they land back on /jobs.
const PENDING_SETTINGS_KEY = "jobr_pending_settings";

const readStoredJson = (storage, key) => {
  try {
    const rawValue = storage.getItem(key);
    return rawValue ? JSON.parse(rawValue) : null;
  } catch (error) {
    return null;
  }
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const pickEmailCandidate = (value = "") => {
  const normalized = typeof value === "string" ? value.trim().toLowerCase() : "";
  return EMAIL_PATTERN.test(normalized) ? normalized : "";
};

const resolveAuthEmail = (identity = {}) =>
  pickEmailCandidate(identity?.email) ||
  pickEmailCandidate(identity?.name) ||
  pickEmailCandidate(identity?.nickname) ||
  pickEmailCandidate(identity?.user_metadata?.email);

const INDUSTRY_SUGGESTIONS = [
  "Artificial Intelligence",
  "Machine Learning",
  "HealthTech",
  "FinTech",
  "ClimateTech",
  "SaaS",
];

const COMPANY_SIZE_SUGGESTIONS = [
  { label: "Startup", insertText: " at a startup" },
  { label: "Scale-up", insertText: " at a scale-up" },
  { label: "Large enterprise", insertText: " at a large enterprise" },
];

const FALLBACK_LOCATION_SUGGESTIONS = [
  "Zurich",
  "Geneva",
  "Basel",
  "Bern",
  "Lausanne",
  "Lucerne",
];

const uniqueStrings = (values = []) =>
  [...new Set(values.map((value) => (value || "").trim()).filter(Boolean))];

const canonicalStringify = (value) =>
  JSON.stringify(value, (_, v) =>
    v && typeof v === "object" && !Array.isArray(v)
      ? Object.keys(v).sort().reduce((sorted, k) => { sorted[k] = v[k]; return sorted; }, {})
      : v
  );

const serializeCriteriaForSave = (criteria = []) =>
  criteria.map((criterion) => ({
    title: criterion.title,
    instruction: criterion.instruction,
    score1_definition: criterion.score1_definition,
    score10_definition: criterion.score10_definition,
  }));

const serializeCriteriaForScoring = (criteria = []) =>
  criteria.map((criterion, index) => ({
    key: `criterion_${index + 1}`,
    title: criterion.title || `Criterion ${index + 1}`,
    instruction: criterion.instruction || "",
    score1_definition: criterion.score1_definition || "",
    score10_definition: criterion.score10_definition || "",
  }));

const mapCriteriaForEditor = (criteria = []) =>
  criteria.map((criterion, index) => ({
    id: index + 1,
    title: criterion.title || "",
    instruction: criterion.instruction || "",
    score1_definition: criterion.score1_definition || "",
    score10_definition: criterion.score10_definition || "",
  }));

const buildCriteriaSnapshot = (criteria = [], dreamJobDescription = "") =>
  JSON.stringify({
    criteria,
    dreamJobDescription: dreamJobDescription || "",
  });

const MAX_DREAM_HISTORY_ITEMS = 10;

const normalizeDreamText = (value = "") =>
  typeof value === "string" ? value.trim() : "";

const buildDreamHistoryEntry = ({
  dreamJobDescription,
  criteria = [],
  omittedDimensions = [],
  scoringPrompt = "",
  savedAt,
} = {}) => {
  const normalizedDreamJobDescription = normalizeDreamText(dreamJobDescription);
  if (!normalizedDreamJobDescription) {
    return null;
  }

  const serializedCriteria = serializeCriteriaForSave(criteria);

  return {
    dreamJobDescription: normalizedDreamJobDescription,
    criteria: serializedCriteria,
    omittedDimensions: Array.isArray(omittedDimensions) ? omittedDimensions : [],
    scoringPrompt:
      typeof scoringPrompt === "string"
        ? scoringPrompt
        : serializedCriteria.length
          ? JSON.stringify(serializedCriteria)
          : "",
    savedAt:
      typeof savedAt === "string" && savedAt.trim()
        ? savedAt
        : new Date().toISOString(),
  };
};

const normalizeDreamHistory = (entries = []) => {
  const seen = new Set();

  return (Array.isArray(entries) ? entries : [])
    .map((entry) => buildDreamHistoryEntry(entry))
    .filter((entry) => {
      if (!entry) {
        return false;
      }

      const key = entry.dreamJobDescription.toLowerCase();
      if (seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    })
    .slice(0, MAX_DREAM_HISTORY_ITEMS);
};

const mergeDreamHistoryEntries = ({
  history = [],
  entry = null,
  excludeDreamJobDescription = "",
} = {}) => {
  const excludedKey = normalizeDreamText(excludeDreamJobDescription).toLowerCase();

  return normalizeDreamHistory([entry, ...(Array.isArray(history) ? history : [])]).filter(
    (historyEntry) => historyEntry.dreamJobDescription.toLowerCase() !== excludedKey
  );
};

const formatDreamHistorySavedAt = (savedAt) => {
  const date = new Date(savedAt);
  if (Number.isNaN(date.getTime())) {
    return "Saved dream";
  }

  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
};

const SCORE_LOOKBACK_DAYS = 10;
const SCORE_LOOKBACK_MS = SCORE_LOOKBACK_DAYS * 24 * 60 * 60 * 1000;
const SCORE_TICKS = Array.from({ length: 11 }, (_, index) => index);

// Time-range filter for the job list toolbar: distinguish jobs published in
// the last 24 hours from the last 7 days. "Any time" keeps the default view
// (everything the API returns within its 30-day window).
const TIME_RANGE_OPTIONS = [
  { value: "all", label: "Any time" },
  { value: "24h", label: "Last 24 hours" },
  { value: "7d", label: "Last 7 days" },
];

const TIME_RANGE_LOOKBACK_MS = {
  "24h": 24 * 60 * 60 * 1000,
  "7d": 7 * 24 * 60 * 60 * 1000,
};

const parseJobTimestamp = (value) => {
  if (!value) return 0;
  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const isRecentJob = (job = {}, lookbackMs = SCORE_LOOKBACK_MS) => {
  const publicationTimestamp = parseJobTimestamp(job.publication_date);
  if (!publicationTimestamp) return false;
  return Date.now() - publicationTimestamp <= lookbackMs;
};

const normalizeScoreRecord = (score) => {
  if (!score || typeof score !== "object") {
    return undefined;
  }

  const detailValues = Object.values(score.details || {})
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value));

  const rawScore =
    score.score ?? score.total_score ?? score.totalScore ?? score["Total Score"] ?? null;
  const numericScore = Number(rawScore);
  const derivedScore = detailValues.length
    ? detailValues.reduce((sum, value) => sum + value, 0) / detailValues.length
    : null;

  return {
    ...score,
    score: Number.isFinite(derivedScore)
      ? Math.max(0, Math.min(10, derivedScore))
      : Number.isFinite(numericScore)
        ? numericScore
        : rawScore,
    criteria_snapshot: Array.isArray(score.criteria_snapshot) ? score.criteria_snapshot : [],
  };
};

const buildLocationChipSuggestions = (value, locationNames = []) => {
  const match = value.match(/^(.*\b(?:in|near)\s*)([a-zA-ZÀ-ÿ-]*)$/i);
  if (!match) return [];

  const [, prefix, typedRaw = ""] = match;
  const typed = typedRaw.toLowerCase();
  const candidates = locationNames
    .filter((city) => city.toLowerCase().startsWith(typed))
    .filter((city) => city.toLowerCase() !== typed)
    .slice(0, 4);

  return candidates.map((city) => ({
    id: `location-${city}`,
    kind: "location",
    label: city,
    prefix,
  }));
};

const buildCompanyChipSuggestions = (value, companyNames = []) => {
  const match = value.match(/^(.*\b(?:at|for)\s*)([a-zA-Z0-9&'.-]*)$/i);
  if (!match) return [];

  const [, prefix, typedRaw = ""] = match;
  const typed = typedRaw.toLowerCase();
  const candidates = companyNames
    .filter((company) => company.toLowerCase().startsWith(typed))
    .filter((company) => company.toLowerCase() !== typed)
    .slice(0, 4);

  return candidates.map((company) => ({
    id: `company-${company}`,
    kind: "company",
    label: company,
    prefix,
  }));
};

const buildIndustryChipSuggestions = (value) => {
  const normalized = value.toLowerCase();
  const hasIndustryAlready = INDUSTRY_SUGGESTIONS.some((industry) =>
    normalized.includes(industry.toLowerCase())
  );

  if (hasIndustryAlready || normalized.trim().length < 24) {
    return [];
  }

  return INDUSTRY_SUGGESTIONS.slice(0, 2).map((industry) => ({
    id: `industry-${industry}`,
    kind: "industry",
    label: industry,
    insertText: ` in ${industry}`,
  }));
};

const hasLocationSignal = (value, locationNames = []) => {
  const normalized = value.toLowerCase();
  return (
    /\b(remote|hybrid|onsite|on-site|in\s+[a-zà-ÿ-]+|near\s+[a-zà-ÿ-]+)\b/i.test(value) ||
    locationNames.some((location) => normalized.includes(location.toLowerCase()))
  );
};

const hasCompanySizeSignal = (value) =>
  /\b(startup|start-up|scale[- ]?up|enterprise|large company|small company|sme|mid[- ]?size|small team|large team)\b/i.test(
    value
  );

const hasCompanySignal = (value, companyNames = []) => {
  const normalized = value.toLowerCase();
  return (
    /\b(at|for)\s+[a-z0-9]/i.test(value) ||
    /\b(company|organisation|organization|employer|firm|brand)\b/i.test(value) ||
    companyNames.some((company) => normalized.includes(company.toLowerCase()))
  );
};

const buildMissingDimensionChips = (value, locationNames = [], companyNames = []) => {
  const normalized = value.trim();
  if (normalized.length < 24) {
    return [];
  }

  const industryChips = buildIndustryChipSuggestions(value);
  const chips = [];

  if (!hasLocationSignal(value, locationNames)) {
    const locationPool = uniqueStrings([...FALLBACK_LOCATION_SUGGESTIONS, ...locationNames]);
    chips.push(
      ...locationPool.slice(0, 1).map((city) => ({
        id: `suggest-location-${city}`,
        kind: "location",
        label: city,
        insertText: ` in ${city}`,
      }))
    );
  }

  if (!hasCompanySizeSignal(value)) {
    chips.push(
      ...COMPANY_SIZE_SUGGESTIONS.slice(0, 2).map((size) => ({
        id: `company-size-${size.label}`,
        kind: "company-size",
        label: size.label,
        insertText: size.insertText,
      }))
    );
  }

  if (!hasCompanySignal(value, companyNames) && companyNames.length) {
    chips.push({
      id: `suggest-company-${companyNames[0]}`,
      kind: "company",
      label: companyNames[0],
      insertText: ` at ${companyNames[0]}`,
    });
  }

  return [...industryChips, ...chips].slice(0, 4);
};

const buildSmartSuggestionChips = (value, locationNames = [], companyNames = []) => {
  const locationContextMatch = value.match(
    /^(.*\b(?:in|near)\s*)([a-zA-ZÀ-ÿ-]*)$/i
  );
  if (locationContextMatch) {
    const locationChips = buildLocationChipSuggestions(value, locationNames);
    if (locationChips.length) return locationChips;

    const [, prefix, typedRaw = ""] = locationContextMatch;
    const typed = typedRaw.toLowerCase();
    const fallbackLocationPool = uniqueStrings([
      ...locationNames,
      ...FALLBACK_LOCATION_SUGGESTIONS,
    ]);

    return fallbackLocationPool
      .filter((city) => city.toLowerCase().startsWith(typed))
      .filter((city) => city.toLowerCase() !== typed)
      .slice(0, 4)
      .map((city) => ({
        id: `fallback-location-${city}`,
        kind: "location",
        label: city,
        prefix,
      }));
  }

  const companyContextMatch = value.match(
    /^(.*\b(?:at|for)\s*)([a-zA-Z0-9&'.-]*)$/i
  );
  if (companyContextMatch) {
    return buildCompanyChipSuggestions(value, companyNames);
  }

  return buildMissingDimensionChips(value, locationNames, companyNames);
};

const applySuggestionValue = (currentValue, chip) => {
  if (!chip) return currentValue;

  if (chip.insertText) {
    const trimmed = currentValue.trimEnd();
    return trimmed ? `${trimmed}${chip.insertText}` : chip.insertText.trimStart();
  }

  if (chip.kind === "location" || chip.kind === "company") {
    return `${chip.prefix}${chip.label}`;
  }

  const trimmed = currentValue.trimEnd();
  return `${trimmed} in ${chip.label}`;
};

const JobList = () => {
  const {
    loginWithRedirect,
    logout,
    user: auth0User,
    isAuthenticated,
    getIdTokenClaims,
  } = useAuth0();
  const hasAuth0Config = Boolean(
    process.env.REACT_APP_AUTH0_DOMAIN?.trim() &&
      process.env.REACT_APP_AUTH0_CLIENT_ID?.trim()
  );
  const isAuth0Available =
    hasAuth0Config &&
    typeof loginWithRedirect === "function" &&
    typeof logout === "function";

  // State Variables
  const [jobs, setJobs] = useState([]);
  const [jobsError, setJobsError] = useState(null);
  const [userInfo, setUserInfo] = useState();
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [showPresets, setShowPresets] = useState(false);
  const [favoriteCompanies, setFavoriteCompanies] = useState("");
  const [terms, setTerms] = useState("");
  const [sort, setSort] = useState(null); // Initialize as null
  const [timeRange, setTimeRange] = useState(TIME_RANGE_OPTIONS[0]);
  const [user, setUser] = useState();
  const [showMenu, setShowMenu] = useState(false);
  const [showNav, setShowNav] = useState(false); // State for hamburger menu
  const [scoringPrompt, setScoringPrompt] = useState("");
  const [isSkillsCollapsed, setIsSkillsCollapsed] = useState(true); // For collapsible skills
  const [criteria, setCriteria] = useState([]);
  const [promptPreview, setPromptPreview] = useState("");
  const [dreamJobDescription, setDreamJobDescription] = useState("");
  const [isCreatingCriteria, setIsCreatingCriteria] = useState(false);
  const [criteriaError, setCriteriaError] = useState(null);
  const [omittedDimensions, setOmittedDimensions] = useState([]);
  const [isSavingCriteria, setIsSavingCriteria] = useState(false);
  const [isAutoSavingCriteria, setIsAutoSavingCriteria] = useState(false);
  const [criteriaSaveMessage, setCriteriaSaveMessage] = useState("");
  const [isRefreshingScores, setIsRefreshingScores] = useState(false);
  const [scoreRefreshMessage, setScoreRefreshMessage] = useState("");
  const [hasLoadedLocalDraft, setHasLoadedLocalDraft] = useState(false);
  const [minMatchScore, setMinMatchScore] = useState(0);
  const [maxMatchScore, setMaxMatchScore] = useState(10);
  const [emailFrequency, setEmailFrequency] = useState("daily");
  const [pendingDreamJobSuggestion, setPendingDreamJobSuggestion] = useState(null);
  const [showDreamHistoryMenu, setShowDreamHistoryMenu] = useState(false);

  const userHasChosenSortRef = useRef(false);
  const criteriaAutosaveTimeoutRef = useRef(null);
  const hasHydratedCriteriaRef = useRef(false);
  const lastSavedCriteriaSnapshotRef = useRef("");
  const scoreRefreshAttemptedRef = useRef(false);
  const lastAutoScoreKeyRef = useRef("");
  const dreamJobTextareaRef = useRef(null);
  const navigate = useNavigate(); // Initialize useNavigate hook
  const location = useLocation();
  const fetchJobsVersionRef = useRef(0);

  // Job id from a digest-email deep link (?id=<jobId>). Derived from the
  // router location so the job list re-filters whenever the URL changes.
  const urlJobId = useMemo(
    () => new URLSearchParams(location.search).get("id"),
    [location.search]
  );

  // Settings deep-link value from the digest footer
  // (?settings=email-frequency), also derived from the router location.
  const urlSettings = useMemo(
    () => new URLSearchParams(location.search).get("settings"),
    [location.search]
  );

  // State for tracking the number of visible jobs
  const [visibleJobsCount, setVisibleJobsCount] = useState(10);

  // State for Profile Sidebar
  const [isProfileSidebarOpen, setIsProfileSidebarOpen] = useState(false);
  const [showCriteriaEditor, setShowCriteriaEditor] = useState(true);
  const [showAnalytics, setShowAnalytics] = useState(false);

  // Initialize Netlify Identity and handle user state
  useEffect(() => {
    if (AUTH_BYPASS) {
      setUser(getDevUser());
      return;
    }

    if (E2E_AUTH_MODE) {
      const shouldForceAuth = sessionStorage.getItem(E2E_AUTH_FLAG) === "1";
      setUser(shouldForceAuth ? getDevUser() : null);
      return;
    }

    if (DEV_MODE) {
      // Faking a logged-in user for development purposes
      setUser(getDevUser());
      return;
    }

    let isMounted = true;

    if (isAuthenticated && auth0User) {
      const hydrateAuthUser = async () => {
        let email = resolveAuthEmail(auth0User);

        if (!email && typeof getIdTokenClaims === "function") {
          try {
            const idTokenClaims = await getIdTokenClaims();
            email = resolveAuthEmail(idTokenClaims);
          } catch (error) {
            console.error("Error resolving Auth0 email from token claims:", error);
          }
        }

        if (!isMounted) return;

        setUser({
          id: auth0User.sub || email || "auth0_user",
          email,
          user_metadata: {
            full_name:
              auth0User.name || auth0User.nickname || email || "myMon.day user",
            email,
          },
        });
      };

      hydrateAuthUser();

      return () => {
        isMounted = false;
      };
    }

    setUser(null);
    return () => {
      isMounted = false;
    };
  }, [auth0User, getIdTokenClaims, isAuthenticated]);

  // Redirect: authenticated → /jobs, unauthenticated → /
  // Deep links from digest emails (?id=<jobId>) must survive these redirects:
  // the query string is carried along, and because the auth redirect returns
  // to the site root without it, the id is parked in sessionStorage when seen
  // and re-attached to /jobs once the user is authenticated. The settings
  // deep link (?settings=email-frequency) is parked the same way.
  useEffect(() => {
    if (user && location.pathname === "/") {
      const pendingJobId = urlJobId || sessionStorage.getItem(PENDING_JOB_ID_KEY);
      const pendingSettings =
        urlSettings || sessionStorage.getItem(PENDING_SETTINGS_KEY);
      sessionStorage.removeItem(PENDING_JOB_ID_KEY);
      sessionStorage.removeItem(PENDING_SETTINGS_KEY);

      // Compose the /jobs query from what is already in the URL plus the
      // parked deep-link values (which win on collision).
      const targetParams = new URLSearchParams(location.search);
      if (pendingJobId) {
        targetParams.set("id", pendingJobId);
      }
      if (pendingSettings) {
        targetParams.set("settings", pendingSettings);
      }
      const targetSearch = targetParams.toString();

      navigate(
        {
          pathname: "/jobs",
          search: targetSearch ? `?${targetSearch}` : "",
        },
        { replace: true }
      );
      return;
    }

    if (!user && location.pathname === "/jobs") {
      navigate({ pathname: "/", search: location.search }, { replace: true });
      return;
    }

    if (urlJobId) {
      sessionStorage.setItem(PENDING_JOB_ID_KEY, urlJobId);
    } else if (user && location.pathname === "/jobs") {
      // The deep link was consumed or navigated away from → forget it so it
      // does not force-filter a later visit.
      sessionStorage.removeItem(PENDING_JOB_ID_KEY);
    }

    if (urlSettings) {
      sessionStorage.setItem(PENDING_SETTINGS_KEY, urlSettings);
    } else if (user && location.pathname === "/jobs") {
      sessionStorage.removeItem(PENDING_SETTINGS_KEY);
    }
  }, [user, urlJobId, urlSettings, location.pathname, location.search, navigate]);

  // Fetch user data when user is authenticated
  useEffect(() => {
    if (!user?.id) return;

    const syncUserData = async () => {
      const persistedUserData = await fetchUserData(user.id);
      const migrationResult = await migrateLocalDataToDB(persistedUserData);
      let hydratedUserData = persistedUserData;

      if (migrationResult.didMigrate) {
        hydratedUserData = await fetchUserData(user.id);
      }

      const shouldSyncIdentityData = Boolean(
        user?.email &&
          (hydratedUserData?.email !== user.email ||
            hydratedUserData?.user_metadata?.full_name !==
              user?.user_metadata?.full_name)
      );

      if (shouldSyncIdentityData) {
        await axios.post("/.netlify/functions/saveUserData", {
          userId: user.id,
          email: user.email,
          user_metadata: {
            full_name:
              user?.user_metadata?.full_name ||
              hydratedUserData?.user_metadata?.full_name ||
              user.email,
          },
        });
        hydratedUserData = await fetchUserData(user.id);
      }

      const persistedPendingSuggestion = readStoredJson(
        sessionStorage,
        PENDING_EXISTING_DREAM_SUGGESTION_KEY
      );
      const pendingSuggestion =
        migrationResult.pendingDraftSuggestion || persistedPendingSuggestion;
      const hasPendingSuggestion = Boolean(
        pendingSuggestion?.dreamJobDescription?.trim()
      );

      if (hasPendingSuggestion) {
        const normalizedSuggestion = {
          dreamJobDescription: pendingSuggestion.dreamJobDescription.trim(),
        };
        sessionStorage.setItem(
          PENDING_EXISTING_DREAM_SUGGESTION_KEY,
          JSON.stringify(normalizedSuggestion)
        );
        setPendingDreamJobSuggestion(normalizedSuggestion);
      } else {
        sessionStorage.removeItem(PENDING_EXISTING_DREAM_SUGGESTION_KEY);
        setPendingDreamJobSuggestion(null);
      }

      const shouldAutoGenerateAfterSignup =
        sessionStorage.getItem(PENDING_SIGNUP_AUTO_GENERATE_FLAG) === "1" ||
        migrationResult.shouldAutoGenerateCriteria === true;

      if (
        shouldAutoGenerateAfterSignup &&
        !hasPendingSuggestion &&
        hydratedUserData?.dreamJobDescription?.trim() &&
        !hydratedUserData?.criteria?.length
      ) {
        const generated = await generateCriteria({
          descriptionOverride: hydratedUserData.dreamJobDescription,
        });

        if (generated) {
          sessionStorage.removeItem(PENDING_SIGNUP_AUTO_GENERATE_FLAG);
          await fetchUserData(user.id);
        }
        return;
      }

      if (
        shouldAutoGenerateAfterSignup &&
        (hydratedUserData?.criteria?.length || hasPendingSuggestion)
      ) {
        sessionStorage.removeItem(PENDING_SIGNUP_AUTO_GENERATE_FLAG);
      }
    };

    syncUserData();
  }, [user?.id]);

  useEffect(() => {
    if (!user || !showCriteriaEditor) {
      return;
    }

    const textarea = dreamJobTextareaRef.current;
    if (!textarea) {
      return;
    }

    textarea.style.height = "0px";
    textarea.style.height = `${Math.max(textarea.scrollHeight, 88)}px`;
  }, [dreamJobDescription, showCriteriaEditor, user]);

  // Fetch jobs on component mount
  useEffect(() => {
    fetchJobs(user?.id);
  }, [user?.id]);

  // Deep link fallback: digest emails link to jobs via ?id=<jobId>. If the
  // linked job is not part of the fetched list (e.g. it sits beyond the list
  // limit), load it directly so the link still opens the job. Jobs older
  // than 30 days are deliberately NOT loaded: getJobById answers 404 for
  // them (nothing published more than 30 days ago is shown in the app or
  // the emails), and that 404 is an expected outcome, not an error.
  useEffect(() => {
    if (!urlJobId) return;
    if (jobs.some((job) => job._id === urlJobId)) return;

    const controller = new AbortController();
    axios
      .get(`/.netlify/functions/getJobById`, {
        params: {
          jobId: urlJobId,
          ...(user?.id ? { userId: user.id } : {}),
        },
        signal: controller.signal,
      })
      .then((response) => {
        const job = response?.data;
        if (!job?._id) return;
        setJobs((prevJobs) =>
          prevJobs.some((existing) => existing._id === job._id)
            ? prevJobs
            : [
                ...prevJobs,
                { ...job, score_user: normalizeScoreRecord(job.score_user) },
              ]
        );
      })
      .catch((error) => {
        // 404 = the job no longer exists or was published more than 30 days
        // ago → expected, nothing to log.
        if (error?.response?.status === 404) return;
        if (error?.name !== "CanceledError") {
          console.error("Error fetching deep-linked job:", error);
        }
      });

    return () => controller.abort();
  }, [urlJobId, user?.id, jobs]);

  // Update scoring prompt preview whenever criteria or scoringPrompt changes
  useEffect(() => {
    generatePromptPreview();
  }, [criteria, scoringPrompt]);

  // Initialize selected prompt template with default category if needed
  useEffect(() => {
    // updatePromptTemplate(promptCategories[selectedCategory]); // Ensure promptCategories is defined
    // This line is commented out because 'promptCategories' is undefined
    // You may need to define 'promptCategories' or handle it differently
  }, [favoriteCompanies]);

  const openSignIn = () => {
    if (AUTH_BYPASS) {
      setUser(getDevUser());
      return;
    }

    if (E2E_AUTH_MODE) {
      if (!user) {
        saveLocalData();
      }
      sessionStorage.setItem(E2E_AUTH_FLAG, "1");
      setUser(getDevUser());
      return;
    }

    if (!user) {
      saveLocalData();
    }

    if (!isAuth0Available) {
      alert(
        "Auth0 is not configured in the frontend. Set REACT_APP_AUTH0_DOMAIN and REACT_APP_AUTH0_CLIENT_ID, then restart npm run dev."
      );
      return;
    }

    loginWithRedirect({
      authorizationParams: {
        screen_hint: "login",
        scope: "openid profile email",
      },
    });
  };

  const openSignUp = () => {
    if (AUTH_BYPASS) {
      setUser(getDevUser());
      return;
    }

    if (E2E_AUTH_MODE) {
      if (!user) {
        saveLocalData({ shouldAutoGenerateCriteria: true });
      }
      sessionStorage.setItem(PENDING_SIGNUP_AUTO_GENERATE_FLAG, "1");
      sessionStorage.setItem(E2E_AUTH_FLAG, "1");
      setUser(getDevUser());
      return;
    }

    if (!user) {
      saveLocalData({ shouldAutoGenerateCriteria: true });
      sessionStorage.setItem(PENDING_SIGNUP_AUTO_GENERATE_FLAG, "1");
    }

    if (!isAuth0Available) {
      alert(
        "Auth0 is not configured in the frontend. Set REACT_APP_AUTH0_DOMAIN and REACT_APP_AUTH0_CLIENT_ID, then restart npm run dev."
      );
      return;
    }

    loginWithRedirect({
      authorizationParams: {
        screen_hint: "signup",
        scope: "openid profile email",
      },
    });
  };

  const handleSignOut = async () => {
    if (AUTH_BYPASS) {
      setUser(getDevUser());
      return;
    }

    if (E2E_AUTH_MODE) {
      sessionStorage.removeItem(E2E_AUTH_FLAG);
      setUser(null);
      return;
    }

    try {
      await persistPendingCriteriaChanges({ refreshScores: false });
    } catch (error) {
      console.error("Error saving criteria before logout:", error);
    }

    setShowCriteriaEditor(false);
    setIsProfileSidebarOpen(false);

    if (DEV_MODE) {
      setUser(null);
      return;
    }

    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const refreshUserScores = async ({ silent = false } = {}) => {
    if (!user?.id) return;

    if (!silent) {
      setScoreRefreshMessage("Scoring jobs and re-ranking your list...");
    }
    setIsRefreshingScores(true);

    try {
      const response = await axios.post("/.netlify/functions/scoreJobsForUser", {
        userId: user.id,
      });
      await fetchJobs(user.id);
      setSort({ value: "score", label: "Top Score" });

      const scoredCount = response?.data?.scoredCount;
      const summary =
        typeof scoredCount === "number"
          ? scoredCount > 0
            ? `Scored ${scoredCount} jobs for your saved criteria.`
            : "All visible jobs are already scored for your saved criteria."
          : "Job scores refreshed.";

      if (!silent) {
        setScoreRefreshMessage(summary);
      }
    } catch (error) {
      const detail =
        error?.response?.data?.error || error?.message || "Unknown error";
      if (!silent) {
        setScoreRefreshMessage(`Score refresh failed: ${detail}`);
      }
      console.error("Error refreshing job scores:", error);
    } finally {
      setIsRefreshingScores(false);
    }
  };

  const companyOptions = useMemo(
    () =>
      [...new Set(jobs.map((job) => job.company_name))]
        .sort()
        .map((value) => ({ value, label: value })),
    [jobs]
  );

  const locationNames = useMemo(
    () => uniqueStrings(jobs.map((job) => job.location?.city)).sort(),
    [jobs]
  );

  const companyNames = useMemo(
    () => uniqueStrings(jobs.map((job) => job.company_name)).sort(),
    [jobs]
  );

  const smartSuggestionChips = useMemo(
    () =>
      buildSmartSuggestionChips(
        dreamJobDescription,
        locationNames,
        companyNames
      ),
    [dreamJobDescription, locationNames, companyNames]
  );

  const locationOptions = useMemo(
    () =>
      [...new Set(jobs.map((job) => job.location?.city))]
        .filter((el) => el)
        .sort()
        .map((value) => ({ value, label: value })),
    [jobs]
  );

  const persistedCriteria = useMemo(
    () => serializeCriteriaForSave(criteria),
    [criteria]
  );

  const currentCriteriaSnapshot = useMemo(
    () => buildCriteriaSnapshot(persistedCriteria, dreamJobDescription),
    [persistedCriteria, dreamJobDescription]
  );

  const dreamJobHistory = useMemo(
    () => normalizeDreamHistory(userInfo?.dreamJobHistory),
    [userInfo?.dreamJobHistory]
  );

  const savedDreamHistorySnapshot = useMemo(
    () =>
      buildDreamHistoryEntry({
        dreamJobDescription: userInfo?.dreamJobDescription,
        criteria: Array.isArray(userInfo?.criteria) ? userInfo.criteria : [],
        omittedDimensions: Array.isArray(userInfo?.omittedDimensions)
          ? userInfo.omittedDimensions
          : [],
        scoringPrompt: userInfo?.scoringPrompt || "",
      }),
    [
      userInfo?.criteria,
      userInfo?.dreamJobDescription,
      userInfo?.omittedDimensions,
      userInfo?.scoringPrompt,
    ]
  );

  useEffect(() => {
    if (!dreamJobHistory.length) {
      setShowDreamHistoryMenu(false);
    }
  }, [dreamJobHistory.length]);

  const savedCriteriaForScoring = useMemo(
    () => (Array.isArray(userInfo?.criteria) ? userInfo.criteria : []),
    [userInfo?.criteria]
  );

  const activeCriteriaSignature = useMemo(
    () => savedCriteriaForScoring.length
      ? canonicalStringify(serializeCriteriaForScoring(savedCriteriaForScoring))
      : "",
    [savedCriteriaForScoring]
  );

  const fetchJobs = async (userIdOverride) => {
    const version = ++fetchJobsVersionRef.current;
    try {
      const currentUserId = userIdOverride || user?.id;
      const response = await axios.get(`/.netlify/functions/jobs`, {
        params: currentUserId ? { userId: currentUserId } : undefined,
      });
      if (version !== fetchJobsVersionRef.current) return; // stale response
      const jobsData = Array.isArray(response.data) ? response.data : [];
      setJobsError(null);
      setJobs(
        jobsData.map((job) => ({
          ...job,
          score_user: normalizeScoreRecord(job.score_user),
        }))
      );
    } catch (error) {
      if (version === fetchJobsVersionRef.current) {
        console.error("Error fetching jobs or user data:", error);
        setJobsError(
          error?.response
            ? `Couldn't load jobs (server responded with ${error.response.status}). Please refresh the page or try again later.`
            : "Couldn't load jobs. Please check your connection and refresh the page."
        );
      }
    }
  };

  const jobsWithUserScore = useMemo(() => {
    const _jobs = jobs.map((job) => ({
      ...job,
      score_user: (() => {
        const normalizedScore = normalizeScoreRecord(job.score_user);

        if (!normalizedScore || !activeCriteriaSignature) {
          return normalizedScore;
        }

        const scoreSignature = canonicalStringify(normalizedScore.criteria_snapshot || []);
        return scoreSignature === activeCriteriaSignature ? normalizedScore : undefined;
      })(),
    }));

    if (sort?.value === "publication_date") {
      _jobs.sort((aa, bb) =>
        aa["publication_date"] <= bb["publication_date"] ? 1 : -1
      );
    } else if (sort?.value === "score") {
      _jobs.sort(
        (aa, bb) =>
          (bb["score_user"]?.score || bb.score?.["Total Score"] || 0) -
          (aa["score_user"]?.score || aa.score?.["Total Score"] || 0)
      );
    }
    return _jobs;
  }, [activeCriteriaSignature, jobs, sort]);

  const filteredJobs = useMemo(() => {
    let filtered = jobsWithUserScore;
    if (urlJobId) {
      filtered = filtered.filter((el) => el._id === urlJobId);
    }
    if (selectedCompanies.length) {
      const selectedCompanyNames = selectedCompanies.map(
        (option) => option.value
      );
      filtered = filtered.filter((job) =>
        selectedCompanyNames.includes(job.company_name)
      );
    }
    if (selectedLocations.length) {
      const selectedLocationNames = selectedLocations.map(
        (option) => option.value
      );
      filtered = filtered.filter((job) =>
        selectedLocationNames.includes(job.location.city)
      );
    }
    const timeRangeLookbackMs = TIME_RANGE_LOOKBACK_MS[timeRange?.value];
    if (timeRangeLookbackMs) {
      filtered = filtered.filter((job) => isRecentJob(job, timeRangeLookbackMs));
    }
    if (user && (minMatchScore > 0 || maxMatchScore < 10)) {
      filtered = filtered.filter((job) => {
        const score = Number(job?.score_user?.score);
        if (!Number.isFinite(score)) return minMatchScore === 0;
        return score >= minMatchScore && score <= maxMatchScore;
      });
    }
    return filtered;
  }, [jobsWithUserScore, urlJobId, minMatchScore, maxMatchScore, selectedCompanies, selectedLocations, timeRange, user]);

  const scoredJobs = useMemo(
    () => jobsWithUserScore.filter((job) => Number.isFinite(Number(job?.score_user?.score))),
    [jobsWithUserScore]
  );

  const averageVisibleScore = useMemo(() => {
    const scores = filteredJobs
      .map((job) => Number(job?.score_user?.score))
      .filter((value) => Number.isFinite(value));

    if (!scores.length) {
      return null;
    }

    return scores.reduce((sum, value) => sum + value, 0) / scores.length;
  }, [filteredJobs]);

  const recentScoredJobCount = useMemo(
    () => scoredJobs.filter((job) => isRecentJob(job, 30 * 24 * 60 * 60 * 1000)).length,
    [scoredJobs]
  );

  const scoredTodayCount = useMemo(() => {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    return scoredJobs.filter((job) => {
      const scoredAt = job?.score_user?.scored_at;
      return scoredAt && new Date(scoredAt) >= todayStart;
    }).length;
  }, [scoredJobs]);

  const averageOverallScore = useMemo(() => {
    const scores = scoredJobs
      .map((job) => Number(job?.score_user?.score))
      .filter((v) => Number.isFinite(v));
    return scores.length ? scores.reduce((s, v) => s + v, 0) / scores.length : null;
  }, [scoredJobs]);

  const recentJobsNeedingScore = useMemo(
    () =>
      jobsWithUserScore.filter((job) => {
        if (!isRecentJob(job)) {
          return false;
        }

        const jobSignature = JSON.stringify(job.score_user?.criteria_snapshot || []);
        return !job.score_user || (activeCriteriaSignature && jobSignature !== activeCriteriaSignature);
      }),
    [jobsWithUserScore, activeCriteriaSignature]
  );

  // Reset visibleJobsCount when filteredJobs changes
  useEffect(() => {
    setVisibleJobsCount(10);
  }, [filteredJobs]);

  const fetchUserData = async (userId) => {
    try {
      const response = await axios.get(
        `/.netlify/functions/getUserData?userId=${userId}`
      );
      const userData = response.data;
      if (userData) {
        setUserInfo(userData);
        setFavoriteCompanies(
          userData.favorite_companies
            ?.map((company) => company.label)
            .join(", ") || ""
        );
        setTerms(userData.terms || "");
        setDreamJobDescription(userData.dreamJobDescription || "");
        setScoringPrompt(
          userData.scoringPrompt ||
            (userData.criteria
              ? JSON.stringify(userData.criteria, null, 2)
              : "")
        );
        setEmailFrequency(userData.emailFrequency || (userData.newsletterEnabled === false ? "off" : "daily"));
        setOmittedDimensions(
          Array.isArray(userData.omittedDimensions) ? userData.omittedDimensions : []
        );
        setCriteria(Array.isArray(userData.criteria) ? mapCriteriaForEditor(userData.criteria) : []);

        lastSavedCriteriaSnapshotRef.current = buildCriteriaSnapshot(
          Array.isArray(userData.criteria) ? userData.criteria : [],
          userData.dreamJobDescription || ""
        );
      } else {
        setCriteria([]);
        setScoringPrompt("");
        setOmittedDimensions([]);
        lastSavedCriteriaSnapshotRef.current = buildCriteriaSnapshot([], "");
      }

      hasHydratedCriteriaRef.current = true;
      return userData || null;
    } catch (error) {
      console.error("Error fetching user data:", error);
      return null;
    }
  };

  const saveUserData = async (data) => {
    if (!user) return;
    try {
      const userId = user.id;
      const fallbackEmail = resolveAuthEmail(user);
      const response = await axios.post("/.netlify/functions/saveUserData", {
        userId,
        email: data?.email ?? fallbackEmail,
        ...data,
      });
      setUserInfo((prev) => ({ ...(prev || {}), ...data }));
      return response.data;
    } catch (error) {
      console.error("Error saving user data:", error);
      throw error;
    }
  };

  const handleDreamJobDescriptionChange = (value) => {
    setDreamJobDescription(value);
  };

  const buildNextDreamHistory = (nextDreamJobDescription) =>
    mergeDreamHistoryEntries({
      history: userInfo?.dreamJobHistory,
      entry: savedDreamHistorySnapshot,
      excludeDreamJobDescription: nextDreamJobDescription,
    });

  const continueAnonymousOnboarding = () => {
    if (!dreamJobDescription.trim()) {
      alert("Please describe your dream job before continuing.");
      return;
    }

    setCriteriaError(null);
    openSignUp();
  };

  const dismissPendingDreamJobSuggestion = () => {
    sessionStorage.removeItem(PENDING_EXISTING_DREAM_SUGGESTION_KEY);
    setPendingDreamJobSuggestion(null);
  };

  const acceptPendingDreamJobSuggestion = async () => {
    const suggestedDreamJob = pendingDreamJobSuggestion?.dreamJobDescription?.trim();
    if (!suggestedDreamJob) {
      dismissPendingDreamJobSuggestion();
      return;
    }

    const generated = await generateCriteria({
      descriptionOverride: suggestedDreamJob,
    });

    if (generated) {
      dismissPendingDreamJobSuggestion();
    }
  };

  const applySuggestionChip = (chip = null) => {
    if (!chip) return;
    setDreamJobDescription((current) => applySuggestionValue(current, chip));
  };

  const restoreDreamHistoryEntry = async (historyEntry) => {
    if (!user?.id) return;

    const normalizedEntry = buildDreamHistoryEntry(historyEntry);
    if (!normalizedEntry) return;

    const restoredCriteria = Array.isArray(normalizedEntry.criteria)
      ? normalizedEntry.criteria
      : [];
    const restoredOmittedDimensions = Array.isArray(normalizedEntry.omittedDimensions)
      ? normalizedEntry.omittedDimensions
      : [];
    const restoredScoringPrompt =
      normalizedEntry.scoringPrompt || JSON.stringify(restoredCriteria);
    const nextDreamHistory = buildNextDreamHistory(
      normalizedEntry.dreamJobDescription
    );

    setShowDreamHistoryMenu(false);
    setCriteriaError(null);
    setIsSavingCriteria(true);
    setCriteriaSaveMessage("");

    try {
      await saveUserData({
        dreamJobDescription: normalizedEntry.dreamJobDescription,
        criteria: restoredCriteria,
        omittedDimensions: restoredOmittedDimensions,
        scoringPrompt: restoredScoringPrompt,
        emailFrequency,
        newsletterEnabled: emailFrequency !== "off",
        dreamJobHistory: nextDreamHistory,
      });

      setDreamJobDescription(normalizedEntry.dreamJobDescription);
      setCriteria(mapCriteriaForEditor(restoredCriteria));
      setOmittedDimensions(restoredOmittedDimensions);
      setScoringPrompt(restoredScoringPrompt);
      lastSavedCriteriaSnapshotRef.current = buildCriteriaSnapshot(
        restoredCriteria,
        normalizedEntry.dreamJobDescription
      );
      hasHydratedCriteriaRef.current = true;
      await refreshUserScores({ silent: true });
      setCriteriaSaveMessage("Dream restored. Jobs are being re-scored.");
    } catch (error) {
      console.error("Error restoring dream from history:", error);
      setCriteriaSaveMessage("Failed to restore dream.");
    } finally {
      setIsSavingCriteria(false);
    }
  };

  const handleDreamJobInputKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!user) {
        continueAnonymousOnboarding();
        return;
      }

      if (!isCreatingCriteria) {
        generateCriteria();
      }
    }
  };

  const saveCriteriaUpdates = async ({ silent = false, refreshScores = true } = {}) => {
    if (!user) return;

    const criteriaPayload = serializeCriteriaForSave(criteria);

    if (silent) {
      setIsAutoSavingCriteria(true);
      setCriteriaSaveMessage("Saving changes automatically...");
    } else {
      setIsSavingCriteria(true);
      setCriteriaSaveMessage("");
    }

    try {
      await saveUserData({
        criteria: criteriaPayload,
        dreamJobDescription,
        scoringPrompt: JSON.stringify(criteriaPayload),
        omittedDimensions,
        emailFrequency,
        newsletterEnabled: emailFrequency !== "off",
      });
      localStorage.removeItem("dreamJobData");
      lastSavedCriteriaSnapshotRef.current = buildCriteriaSnapshot(
        criteriaPayload,
        dreamJobDescription
      );
      if (refreshScores) {
        await refreshUserScores({ silent: true });
      }
      setCriteriaSaveMessage(
        silent
          ? refreshScores
            ? "Changes saved automatically. Jobs are being re-scored."
            : "Changes saved automatically."
          : refreshScores
            ? "Criteria saved. Jobs are being re-scored."
            : "Criteria saved."
      );
    } catch {
      setCriteriaSaveMessage(
        silent ? "Auto-save failed. Please try again." : "Failed to save criteria."
      );
    } finally {
      setIsSavingCriteria(false);
      setIsAutoSavingCriteria(false);
    }
  };

  const refreshJob = async (jobId) => {
    try {
      const response = await axios.get(
        `/.netlify/functions/getJobById?jobId=${jobId}${user?.id ? `&userId=${encodeURIComponent(user.id)}` : ""}`
      );
      const updatedJob = {
        ...response.data,
        score_user: normalizeScoreRecord(response.data?.score_user),
      };
      setJobs((prevJobs) =>
        prevJobs.map((job) => (job._id === jobId ? updatedJob : job))
      );
    } catch (error) {
      console.error("Error refreshing job:", error);
    }
  };

  const removeJob = (jobId) => {
    setJobs((prevJobs) => prevJobs.filter((job) => job._id !== jobId));
  };

  const togglePresets = () => setShowPresets(!showPresets);

  const toggleMenu = () => {
    setShowMenu(!showMenu);
  };

  const toggleNav = () => {
    setShowNav(!showNav);
  };

  const setShowNavFalse = () => {
    setShowNav(false);
  };

  const getUserInitials = (username) => {
    if (!username) return "NA";
    const initials = username.slice(0, 2).toUpperCase();
    return initials;
  };

  const handleFavoriteCompaniesChange = (e) => {
    const companies = e.target.value;
    setFavoriteCompanies(companies);
    saveUserData({
      favorite_companies: companies
        .split(",")
        .map((company) => ({ value: company.trim(), label: company.trim() })),
    });
  };

  const handlePromptTemplateChange = (e) => {
    const updatedTemplate = e.target.value;
    setScoringPrompt(updatedTemplate);
    saveUserData({ scoringPrompt: updatedTemplate });
  };

  const handleTerms = async (e) => {
    const _terms = e.target.value;
    if (_terms !== userInfo.terms) {
      await saveUserData({ terms: _terms });
    }
  };

  const toggleSkillsCollapse = () => {
    setIsSkillsCollapsed(!isSkillsCollapsed);
  };

  const FREQ_CYCLE = ["daily", "weekly", "off"];
  const FREQ_LABELS = { daily: "Daily digest", weekly: "Weekly digest", off: "Digest off" };

  const handleEmailFrequencyChange = async (nextFreq) => {
    if (!FREQ_CYCLE.includes(nextFreq) || nextFreq === emailFrequency) {
      return { ok: true };
    }

    const prevFreq = emailFrequency;
    setEmailFrequency(nextFreq);

    if (!user?.id) return { ok: true };

    try {
      await saveUserData({ emailFrequency: nextFreq, newsletterEnabled: nextFreq !== "off" });
      setScoreRefreshMessage(FREQ_LABELS[nextFreq]);
      return { ok: true };
    } catch (error) {
      console.error("Error updating email frequency:", error);
      setEmailFrequency(prevFreq);
      return { ok: false };
    }
  };

  const handleEmailFrequencyToggle = () => {
    const currentIndex = FREQ_CYCLE.indexOf(emailFrequency);
    handleEmailFrequencyChange(FREQ_CYCLE[(currentIndex + 1) % FREQ_CYCLE.length]);
  };

  // Dynamically generate sort options based on user login
  const OPTIONS_SORT = useMemo(() => {
    const options = [{ value: "publication_date", label: "Recent Date" }];
    if (user) {
      options.push({ value: "score", label: "Top Score" });
    }
    return options;
  }, [user]);

  // Ensure the current sort option is valid; auto-switch to Top Score when scoring becomes available
  useEffect(() => {
    if (!user) {
      if (sort?.value === "score") {
        setSort(OPTIONS_SORT[0]);
        userHasChosenSortRef.current = false;
      }
    } else if (user && userInfo?.criteria?.length && !userHasChosenSortRef.current) {
      setSort(OPTIONS_SORT.find((o) => o.value === "score") || OPTIONS_SORT[0]);
      userHasChosenSortRef.current = true;
    } else if (!sort) {
      setSort(OPTIONS_SORT[0]);
    }
  }, [user, userInfo?.criteria, OPTIONS_SORT]);

  // Email deep links: "?settings=email-frequency" (from the digest footer)
  // and "?unsubscribe=1" open the Profile panel, which hosts the job digest
  // frequency control. The params are stripped afterwards so a refresh or
  // back-navigation restores the default panel state.
  useEffect(() => {
    if (!user) return;

    const params = new URLSearchParams(window.location.search);
    const isEmailSettingsLink =
      params.get("settings") === "email-frequency" || params.get("unsubscribe") === "1";
    if (!isEmailSettingsLink) return;

    setShowCriteriaEditor(false);
    setShowAnalytics(false);
    setIsProfileSidebarOpen(true);

    // Strip the params via the router (not history.replaceState) so React
    // Router notices, the parking effect above clears the sessionStorage
    // fallback, and a later login does not re-open the panel.
    params.delete("settings");
    params.delete("unsubscribe");
    const nextQuery = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextQuery ? `?${nextQuery}` : "",
      },
      { replace: true }
    );
  }, [user, location.search, location.pathname, navigate]);

  useEffect(() => {
    if (!user?.id || !showCriteriaEditor || !hasHydratedCriteriaRef.current) {
      return undefined;
    }

    if (currentCriteriaSnapshot === lastSavedCriteriaSnapshotRef.current) {
      return undefined;
    }

    setCriteriaSaveMessage("Changes pending...");
    if (criteriaAutosaveTimeoutRef.current) {
      clearTimeout(criteriaAutosaveTimeoutRef.current);
    }

    criteriaAutosaveTimeoutRef.current = window.setTimeout(() => {
      saveCriteriaUpdates({ silent: true });
    }, 1200);

    return () => {
      if (criteriaAutosaveTimeoutRef.current) {
        clearTimeout(criteriaAutosaveTimeoutRef.current);
      }
    };
  }, [currentCriteriaSnapshot, user?.id, showCriteriaEditor]);

  const persistPendingCriteriaChanges = async ({ refreshScores = true } = {}) => {
    if (!user?.id) return false;

    if (criteriaAutosaveTimeoutRef.current) {
      clearTimeout(criteriaAutosaveTimeoutRef.current);
      criteriaAutosaveTimeoutRef.current = null;
    }

    if (currentCriteriaSnapshot === lastSavedCriteriaSnapshotRef.current) {
      return false;
    }

    await saveCriteriaUpdates({ silent: true, refreshScores });
    return true;
  };

  const closeCriteriaEditor = async () => {
    await persistPendingCriteriaChanges();
    setShowCriteriaEditor(false);
  };

  // Handler functions for Criteria
  const addCriteria = () => {
    const newId = criteria.length ? criteria[criteria.length - 1].id + 1 : 1;
    setCriteria([
      ...criteria,
      {
        id: newId,
        title: "",
        instruction: "",
        score1_definition: "",
        score10_definition: "",
      },
    ]);
  };

  const removeCriteria = (id) => {
    setCriteria(criteria.filter((criterion) => criterion.id !== id));
  };

  const handleCriteriaChange = (id, field, value) => {
    setCriteria(
      criteria.map((criterion) =>
        criterion.id === id ? { ...criterion, [field]: value } : criterion
      )
    );
  };

  // Generate Prompt Preview
  const generatePromptPreview = () => {
    if (criteria.length === 0) {
      setPromptPreview("No criteria defined.");
      return;
    }

    const criteriaDescriptions = criteria
      .map((criterion) => {
        return `**${criterion.title}**\n${criterion.instruction}\nScore 1: ${criterion.score1_definition}\nScore 10: ${criterion.score10_definition}\n`;
      })
      .join("\n");

    const prompt = `Please evaluate each job ad based on the following criteria. Provide a score from 1 to 10 for each criterion, where 1 is the lowest and 10 is the highest.

${criteriaDescriptions}

Ensure that the scoring is consistent and based on the provided information.`;

    setPromptPreview(prompt);
  };

  // Function to generate criteria based on dream job description
  const generateCriteria = async ({ descriptionOverride } = {}) => {
    const descriptionToUse = (descriptionOverride ?? dreamJobDescription).trim();

    if (!descriptionToUse) {
      alert("Please describe your dream job before generating criteria.");
      return false;
    }

    setIsCreatingCriteria(true);
    setCriteriaError(null);

    try {
      const response = await axios.post("/.netlify/functions/generateCriteria", {
        dreamJobDescription: descriptionToUse,
      });

      const {
        criteria: generatedCriteria,
        omittedDimensions: nextOmittedDimensions = [],
      } = response.data;

      // Update the criteria state with the generated criteria
      setCriteria(
        generatedCriteria.map((criterion, index) => ({
          id: index + 1,
          title: criterion.title,
          instruction: criterion.instruction,
          score1_definition: criterion.score1_definition,
          score10_definition: criterion.score10_definition,
        }))
      );
      setOmittedDimensions(
        Array.isArray(nextOmittedDimensions) ? nextOmittedDimensions : []
      );
      setDreamJobDescription(descriptionToUse);

      // Update the scoringPrompt in the state and database
      setScoringPrompt(JSON.stringify(generatedCriteria, null, 2));
      if (user) {
        const nextDreamHistory = buildNextDreamHistory(descriptionToUse);
        await saveUserData({
          scoringPrompt: JSON.stringify(generatedCriteria),
          dreamJobDescription: descriptionToUse,
          criteria: generatedCriteria,
          omittedDimensions: nextOmittedDimensions,
          emailFrequency,
          newsletterEnabled: emailFrequency !== "off",
          dreamJobHistory: nextDreamHistory,
        });
        lastSavedCriteriaSnapshotRef.current = buildCriteriaSnapshot(
          generatedCriteria,
          descriptionToUse
        );
        hasHydratedCriteriaRef.current = true;
        await refreshUserScores({ silent: true });
      }

      return true;

    } catch (error) {
      console.error(
        "Error generating criteria:",
        error.response?.data || error.message
      );
      setCriteriaError("Failed to generate criteria. Please try again.");
      return false;
    } finally {
      setIsCreatingCriteria(false);

    }
  };

  // Function to migrate data from localStorage to DB upon sign-in
  const migrateLocalDataToDB = async (existingUserData = null) => {
    try {
      const localData = readStoredJson(localStorage, "dreamJobData");
      if (!localData) {
        return {
          didMigrate: false,
          shouldAutoGenerateCriteria: false,
          pendingDraftSuggestion: null,
        };
      }

      const serverHasCriteria = Boolean(
        existingUserData?.dreamJobDescription?.trim() || existingUserData?.criteria?.length
      );
      const localHasCriteria = Boolean(
        localData?.dreamJobDescription?.trim() || localData?.criteria?.length
      );

      if (!localHasCriteria) {
        localStorage.removeItem("dreamJobData");
        return {
          didMigrate: false,
          shouldAutoGenerateCriteria: false,
          pendingDraftSuggestion: null,
        };
      }

      if (serverHasCriteria) {
        const pendingDraftSuggestion = localData?.dreamJobDescription?.trim()
          ? {
              dreamJobDescription: localData.dreamJobDescription.trim(),
            }
          : null;
        localStorage.removeItem("dreamJobData");
        return {
          didMigrate: false,
          shouldAutoGenerateCriteria: false,
          pendingDraftSuggestion,
        };
      }

      await saveUserData({
        scoringPrompt: localData.scoringPrompt,
        dreamJobDescription: localData.dreamJobDescription,
        criteria: serializeCriteriaForSave(localData.criteria || []),
        omittedDimensions: localData.omittedDimensions || [],
        emailFrequency: localData.emailFrequency || "daily",
        newsletterEnabled: (localData.emailFrequency || "daily") !== "off",
      });
      localStorage.removeItem("dreamJobData");
      return {
        didMigrate: true,
        shouldAutoGenerateCriteria: localData.shouldAutoGenerateCriteria === true,
        pendingDraftSuggestion: null,
      };
    } catch (error) {
      console.error("Error migrating local data to DB:", error);
      return {
        didMigrate: false,
        shouldAutoGenerateCriteria: false,
        pendingDraftSuggestion: null,
      };
    }
  };

  // Function to save data locally for anonymous users
  const saveLocalData = ({ shouldAutoGenerateCriteria = false } = {}) => {
    const dataToSave = {
      scoringPrompt: "",
      dreamJobDescription,
      criteria: [],
      omittedDimensions: [],
      emailFrequency,
      shouldAutoGenerateCriteria,
    };
    localStorage.setItem("dreamJobData", JSON.stringify(dataToSave));
  };

  // Monitor local draft and save locally if not authenticated.
  // Wait until local draft hydration has completed to avoid overwriting stored draft with defaults.
  useEffect(() => {
    if (!user && hasLoadedLocalDraft) {
      const persistedLocalDraft = readStoredJson(localStorage, "dreamJobData");
      const dataToSave = {
        scoringPrompt: "",
        dreamJobDescription,
        criteria: [],
        omittedDimensions: [],
        emailFrequency,
        shouldAutoGenerateCriteria:
          persistedLocalDraft?.shouldAutoGenerateCriteria === true,
      };
      localStorage.setItem("dreamJobData", JSON.stringify(dataToSave));
    }
  }, [dreamJobDescription, emailFrequency, user, hasLoadedLocalDraft]);

  // Function to load data from localStorage for anonymous users
  const loadLocalData = () => {
    const localData = JSON.parse(localStorage.getItem("dreamJobData"));
    if (localData) {
      setScoringPrompt("");
      setDreamJobDescription(localData.dreamJobDescription || "");
      setEmailFrequency(localData.emailFrequency || "daily");
      setOmittedDimensions([]);
      setCriteria([]);
    }
  };

  // Load local data on component mount if not authenticated
  useEffect(() => {
    if (user) {
      setHasLoadedLocalDraft(false);
      return;
    }

    if (!user) {
      loadLocalData();
      setHasLoadedLocalDraft(true);
    }
  }, [user]);

  useEffect(() => {
    if (!user?.id || !savedCriteriaForScoring.length) {
      scoreRefreshAttemptedRef.current = false;
      lastAutoScoreKeyRef.current = "";
      return;
    }

    if (!jobs.length || !recentJobsNeedingScore.length || !activeCriteriaSignature) {
      scoreRefreshAttemptedRef.current = false;
      return;
    }

    const autoScoreKey = `${user.id}:${activeCriteriaSignature}:${recentJobsNeedingScore
      .map((job) => job._id)
      .join(",")}`;

    if (
      isRefreshingScores ||
      (scoreRefreshAttemptedRef.current && lastAutoScoreKeyRef.current === autoScoreKey)
    ) {
      return;
    }

    scoreRefreshAttemptedRef.current = true;
    lastAutoScoreKeyRef.current = autoScoreKey;
    refreshUserScores({ silent: true });
  }, [
    user?.id,
    savedCriteriaForScoring,
    jobs.length,
    recentJobsNeedingScore,
    activeCriteriaSignature,
    isRefreshingScores,
  ]);

  // Handlers to open the sidebar with different sections
  const handleProfileClick = () => {
    setShowCriteriaEditor(false);
    setShowAnalytics(false);
    setIsProfileSidebarOpen((prev) => !prev);
  };

  const scrollToCriteriaEditor = () => {
    window.setTimeout(() => {
      const criteriaEditorSection = document.getElementById(
        "criteria-editor-section"
      );
      if (criteriaEditorSection) {
        criteriaEditorSection.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 0);
  };

  const handleCriteriaClick = async (event) => {
    if (event) {
      event.preventDefault();
    }

    setIsProfileSidebarOpen(false);
    setShowAnalytics(false);
    const shouldClose = (window.location.pathname === "/" || window.location.pathname === "/jobs") && showCriteriaEditor;
    if (shouldClose) {
      await closeCriteriaEditor();
      return;
    }

    setShowCriteriaEditor(true);
    navigate("/jobs");
    scrollToCriteriaEditor();
  };

  const handleAnalyticsClick = (event) => {
    if (event) {
      event.preventDefault();
    }
    setIsProfileSidebarOpen(false);
    setShowCriteriaEditor(false);
    setShowAnalytics((prev) => !prev);
    navigate("/jobs");
  };

  return (
    <div className="job-list">
      <header className={`header ${showNav ? "active" : ""}`}>
        <div className="hamburger" onClick={toggleNav}>
          {showNav ? <FaTimes /> : <FaBars />}
        </div>
        <div className="logo">
          <h1>myMon.day</h1>
          <p>Find companies that share your values.</p>
        </div>
        <nav className={`nav ${showNav ? "active" : ""}`}>
          <NavLink
            to={user ? "/jobs" : "/"}
            className={({ isActive }) => (isActive ? "active" : "")}
            onClick={() => setShowNav(false)}
            end
          >
            {user ? "JOBS" : "HOME"}
          </NavLink>
          <NavLink
            to="/about"
            className={({ isActive }) => (isActive ? "active" : "")}
            onClick={() => setShowNav(false)}
          >
            ABOUT
          </NavLink>
          {/* Uncomment if needed
          <NavLink
            to="/pricing"
            className={({ isActive }) => (isActive ? "active" : "")}
            onClick={() => setShowNav(false)}
          >
            PRICING
          </NavLink>
          */}
          {/* CRITERIA Menu Item */}
          {user && (
            <NavLink
              to="/jobs"
              className={() => (showCriteriaEditor ? "active" : "")}
              onClick={async (event) => {
                await handleCriteriaClick(event);
                setShowNav(false);
              }}
            >
              CRITERIA
            </NavLink>
          )}
          {user && (
            <NavLink
              to="/jobs"
              className={() => (showAnalytics ? "active" : "")}
              onClick={(event) => {
                handleAnalyticsClick(event);
                setShowNav(false);
              }}
            >
              ANALYTICS
            </NavLink>
          )}
        </nav>
        {user ? (
          <>
            <button
              type="button"
              className={`newsletter-sidebar-button nav-button${emailFrequency !== "off" ? " is-enabled" : ""}`}
              onClick={handleEmailFrequencyToggle}
            >
              {emailFrequency === "daily" ? "✓ Daily" : emailFrequency === "weekly" ? "✓ Weekly" : "Digest off"}
            </button>
            <div className="user-menu">
              <button
                className="avatar nav-button"
                onClick={handleProfileClick}
              >
                {getUserInitials(user.user_metadata.full_name)}
              </button>
              <button className="logout-button nav-button" onClick={handleSignOut}>
                LOGOUT
              </button>
            </div>
          </>
        ) : (
          <button className="logout-button nav-button sign-in-nav-button" onClick={openSignIn}>
            SIGN IN&nbsp;
            <FaArrowRight className="arrow" />
          </button>
        )}
      </header>

      {/* Main Content: Show different sections based on authentication */}
      {!user ? (
        // If user is not authenticated, show Onboarding Steps
        <div className="presets-section presets-section--anonymous">
          {/* Step 1: Describe your Dream Job */}
          <div className="step step--anonymous" id="step-1">
            <h3>Step 1:</h3>
            <h2>Craft your dream job</h2>
          </div>

          {/* Onboarding Section */}
          <div className="onboarding-section criteria-live-editor onboarding-section--anonymous">
            <p className="help-text">
              Craft your dream job and mention the role, industry, favorite
              companies, location and/or corporate culture.
            </p>
            <textarea
              className="dream-job-textarea"
              placeholder="Describe your dream job"
              value={dreamJobDescription}
              onChange={(e) => handleDreamJobDescriptionChange(e.target.value)}
              aria-label="Describe your dream job"
              onKeyDown={handleDreamJobInputKeyDown}
            />
            {smartSuggestionChips.length > 0 && (
              <div className="dream-job-suggestions" role="list">
                {smartSuggestionChips.map((chip) => (
                  <button
                    key={chip.id}
                    type="button"
                    className={`dream-job-chip ${chip.kind}`}
                    onClick={() => applySuggestionChip(chip)}
                  >
                    <span className="chip-type">{chip.kind}</span>
                    <span>{chip.label}</span>
                  </button>
                ))}
              </div>
            )}
            <div className="onboarding-cta-row">
              <button
                type="button"
                onClick={continueAnonymousOnboarding}
                className="sign-in-sign-up-button menu-style-button app-button app-button--primary"
              >
                CONTINUE TO SIGN UP <FaArrowRight className="arrow" />
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="authenticated-content authenticated-dashboard">
          <main className="dashboard-main">
            <div className="bottom-section bottom-section--status-only">
              {(isRefreshingScores || scoreRefreshMessage) && (
                <p className={`help-text status-text${isRefreshingScores ? " status-text--active" : ""}`}>
                  {isRefreshingScores
                    ? "Scoring jobs in the background and re-sorting your matches..."
                    : scoreRefreshMessage}
                </p>
              )}
            </div>

            <section className="dashboard-hero">
              <h2 className="dashboard-hero-title">
                Find companies that share your values and jobs with a purpose!
              </h2>
              <div className="dashboard-hero-chart-shell">
                <JobBarChart jobData={jobsWithUserScore} minScore={minMatchScore} />
              </div>
              <div className="score-slider-panel">
                <div className="score-slider-header">
                  <span>Match score range</span>
                  <strong>{minMatchScore} – {maxMatchScore}</strong>
                </div>
                <div className="score-slider-scale">
                  <div className="dual-range-track">
                    <div
                      className="dual-range-fill"
                      style={{
                        left: `${(minMatchScore / 10) * 100}%`,
                        right: `${100 - (maxMatchScore / 10) * 100}%`,
                      }}
                    />
                    <input
                      type="range"
                      min="0"
                      max="10"
                      step="1"
                      value={minMatchScore}
                      onChange={(event) => {
                        const v = Number(event.target.value);
                        setMinMatchScore(Math.min(v, maxMatchScore));
                      }}
                      className="score-slider-input dual-range-input"
                      aria-label="Minimum match score"
                    />
                    <input
                      type="range"
                      min="0"
                      max="10"
                      step="1"
                      value={maxMatchScore}
                      onChange={(event) => {
                        const v = Number(event.target.value);
                        setMaxMatchScore(Math.max(v, minMatchScore));
                      }}
                      className="score-slider-input dual-range-input"
                      aria-label="Maximum match score"
                    />
                  </div>
                  <div className="score-slider-ticks" aria-hidden="true">
                    {SCORE_TICKS.map((tick) => (
                      <span
                        key={tick}
                        className={`score-slider-tick${tick >= minMatchScore && tick <= maxMatchScore ? " is-active" : ""}`}
                      >
                        {tick}
                      </span>
                    ))}
                  </div>
                </div>
                <p className="dashboard-hero-caption">
                  Showing {filteredJobs.length} jobs{averageVisibleScore !== null ? ` · Avg. match ${averageVisibleScore.toFixed(1)}` : ""}
                </p>
              </div>
            </section>

            <div className="filters-and-sort dashboard-toolbar">
              <div className="filters">
                <Select
                  isMulti
                  name="companies"
                  options={companyOptions}
                  value={selectedCompanies}
                  className="basic-multi-select toolbar-select"
                  classNamePrefix="select"
                  placeholder="All companies"
                  onChange={(options) => setSelectedCompanies(options || [])}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  menuPosition="fixed"
                  styles={darkModeStyles}
                />
                <Select
                  isMulti
                  name="locations"
                  options={locationOptions}
                  value={selectedLocations}
                  className="basic-multi-select toolbar-select"
                  classNamePrefix="select"
                  placeholder="All locations"
                  onChange={(options) => setSelectedLocations(options || [])}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  menuPosition="fixed"
                  styles={darkModeStyles}
                />
                <Select
                  name="timeRange"
                  options={TIME_RANGE_OPTIONS}
                  value={timeRange}
                  className="basic-multi-select toolbar-select time-range-select"
                  classNamePrefix="select"
                  placeholder="Any time"
                  isSearchable={false}
                  onChange={setTimeRange}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  menuPosition="fixed"
                  styles={darkModeStyles}
                />
              </div>
              <div className="sort">
                <Select
                  name="Sort"
                  options={OPTIONS_SORT}
                  className="basic-multi-select toolbar-select"
                  classNamePrefix="select"
                  placeholder="by Recent date"
                  value={sort}
                  onChange={(e) => { userHasChosenSortRef.current = true; setSort(e); }}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  menuPosition="fixed"
                  styles={darkModeStyles}
                />
              </div>
            </div>

            <div className="job-list-container">
              {jobsError ? (
                <p className="help-text">{jobsError}</p>
              ) : filteredJobs.length === 0 && (
                <p className="help-text">
                  {jobs.length > 0
                    ? "No jobs match the current filters."
                    : "No jobs found yet. Jobs appear here once they are added to the database."}
                </p>
              )}
              <ul>
                {filteredJobs.slice(0, visibleJobsCount).map((job, index) => (
                  <JobItem
                    key={job._id}
                    job={job}
                    refreshJob={refreshJob}
                    removeJob={removeJob}
                    userId={user?.id}
                    isRefreshingScores={isRefreshingScores}
                    activeCriteriaSignature={activeCriteriaSignature}
                    rank={index + 1}
                    showRank={sort?.value === "score"}
                  />
                ))}
              </ul>

              {visibleJobsCount < filteredJobs.length && (
                <div className="load-more-container">
                  <button
                    onClick={() => setVisibleJobsCount(visibleJobsCount + 10)}
                    className="load-more-button app-button app-button--secondary"
                  >
                    Load More Jobs
                  </button>
                </div>
              )}
            </div>
          </main>

          <aside className={`dashboard-side-panel${showCriteriaEditor || showAnalytics || isProfileSidebarOpen ? "" : " dashboard-side-panel--collapsed"}`}>
            {showAnalytics && (
              <section className="analytics-panel">
                <h3 className="side-panel-title">Analytics</h3>
                <div className="analytics-stats-grid">
                  <div className="analytics-stat-card">
                    <span className="analytics-stat-value">{jobs.length}</span>
                    <span className="analytics-stat-label">Total jobs</span>
                  </div>
                  <div className="analytics-stat-card">
                    <span className="analytics-stat-value">{scoredJobs.length}</span>
                    <span className="analytics-stat-label">Scored</span>
                  </div>
                  <div className="analytics-stat-card">
                    <span className="analytics-stat-value">{averageOverallScore !== null ? averageOverallScore.toFixed(1) : "–"}</span>
                    <span className="analytics-stat-label">Avg. score</span>
                  </div>
                  <div className="analytics-stat-card">
                    <span className="analytics-stat-value">{scoredTodayCount}</span>
                    <span className="analytics-stat-label">Scored today</span>
                  </div>
                </div>
                <div className="analytics-chart-section">
                  <RadarChart jobData={filteredJobs} />
                </div>
              </section>
            )}

            {isProfileSidebarOpen && (
              <section className="profile-panel">
                <h3 className="side-panel-title">Profile</h3>
                <ProfileSidebar
                  isOpen={true}
                  userId={user?.id}
                  userEmail={user?.email}
                  userName={user?.user_metadata?.full_name}
                  emailFrequency={emailFrequency}
                  onEmailFrequencyChange={handleEmailFrequencyChange}
                />
              </section>
            )}

            {showCriteriaEditor ? (
              <section
                className="onboarding-section criteria-live-editor"
                id="criteria-editor-section"
              >
                <div className="criteria-panel-header">
                  <h3 className="side-panel-title">Your criteria</h3>
                  <p className="help-text criteria-panel-help">
                    Shape how jobs are scored. Changes are auto-saved and your list re-ranks after scoring.
                  </p>
                </div>

                {pendingDreamJobSuggestion?.dreamJobDescription?.trim() && (
                  <div className="criteria-draft-suggestion">
                    <p className="criteria-draft-suggestion-title">
                      You drafted a new dream job before signing in.
                    </p>
                    <p className="help-text criteria-draft-suggestion-copy">
                      Keep your saved dream, or replace it with the new draft below.
                    </p>
                    <div className="criteria-draft-suggestion-preview">
                      {pendingDreamJobSuggestion.dreamJobDescription}
                    </div>
                    <div className="criteria-draft-suggestion-actions">
                      <button
                        type="button"
                        className="app-button app-button--primary"
                        onClick={acceptPendingDreamJobSuggestion}
                      >
                        USE NEW DREAM <FaArrowRight className="arrow" />
                      </button>
                      <button
                        type="button"
                        className="app-button app-button--ghost"
                        onClick={dismissPendingDreamJobSuggestion}
                      >
                        KEEP CURRENT DREAM
                      </button>
                    </div>
                  </div>
                )}

                <div className="criteria-dream-editor">
                  <div className="criteria-dream-editor-toolbar">
                    <div className="criteria-dream-history">
                      <button
                        type="button"
                        className="criteria-dream-history-button app-button app-button--ghost"
                        onClick={() => setShowDreamHistoryMenu((current) => !current)}
                        disabled={!dreamJobHistory.length}
                        aria-label="Dream history"
                      >
                        <FaHistory />
                      </button>
                      {showDreamHistoryMenu && dreamJobHistory.length > 0 && (
                        <div className="criteria-dream-history-menu">
                          {dreamJobHistory.map((historyEntry) => (
                            <button
                              key={`${historyEntry.savedAt}-${historyEntry.dreamJobDescription}`}
                              type="button"
                              className="criteria-dream-history-item"
                              onClick={() => restoreDreamHistoryEntry(historyEntry)}
                            >
                              <span className="criteria-dream-history-item-text">
                                {historyEntry.dreamJobDescription}
                              </span>
                              <span className="criteria-dream-history-item-date">
                                {formatDreamHistorySavedAt(historyEntry.savedAt)}
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <textarea
                    ref={dreamJobTextareaRef}
                    className="dream-job-textarea dream-job-textarea--auto"
                    placeholder="Describe your dream job"
                    value={dreamJobDescription}
                    onChange={(e) => handleDreamJobDescriptionChange(e.target.value)}
                    aria-label="Describe your dream job"
                    onKeyDown={handleDreamJobInputKeyDown}
                    rows={1}
                  />
                </div>

                {!isCreatingCriteria && (
                  <button
                    onClick={generateCriteria}
                    className="generate-criteria-button menu-style-button app-button app-button--primary"
                  >
                    {criteria.length ? "REGENERATE CRITERIA" : "GENERATE CRITERIA"} <FaArrowRight className="arrow" />
                  </button>
                )}

                {isCreatingCriteria && (
                  <div className="loader-container loader-container--inline">
                    <PropagateLoader color="#FFFFFF" size={12} />
                    <p className="loader-text">Generating criteria...</p>
                  </div>
                )}

                {criteriaError && <p className="error-text">{criteriaError}</p>}
                {omittedDimensions.length > 0 && (
                  <div className="criteria-omitted-notice">
                    <p className="help-text criteria-omitted-title">
                      Not scored yet because you did not explicitly mention them in your dream job description.
                    </p>
                    <div className="criteria-omitted-list">
                      {omittedDimensions.map((dimension) => (
                        <div key={dimension.title} className="criteria-omitted-card">
                          <strong>{dimension.title}</strong>
                          <span>{dimension.reason}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {criteria.map((criterion, index) => (
                  <div key={criterion.id} className="criteria-item">
                    <div className="criteria-item-header">
                      <label>Criteria #{index + 1}</label>
                      {criteria.length > 1 && (
                        <button
                          type="button"
                          className="criteria-item-remove-button"
                          onClick={() => removeCriteria(criterion.id)}
                          aria-label={`Remove criterion ${index + 1}`}
                          title="Remove criterion"
                        >
                          <FaTrashAlt />
                        </button>
                      )}
                    </div>
                    <textarea
                      className="criteria-expandable-input"
                      placeholder="Criterion title"
                      value={criterion.title}
                      rows={1}
                      onChange={(e) =>
                        handleCriteriaChange(criterion.id, "title", e.target.value)
                      }
                    />
                    <textarea
                      className="criteria-expandable-input"
                      placeholder="Instruction"
                      value={criterion.instruction}
                      rows={1}
                      onChange={(e) =>
                        handleCriteriaChange(
                          criterion.id,
                          "instruction",
                          e.target.value
                        )
                      }
                    />
                    <textarea
                      className="criteria-expandable-input"
                      placeholder="Score 1 definition"
                      value={criterion.score1_definition}
                      rows={1}
                      onChange={(e) =>
                        handleCriteriaChange(
                          criterion.id,
                          "score1_definition",
                          e.target.value
                        )
                      }
                    />
                    <textarea
                      className="criteria-expandable-input"
                      placeholder="Score 10 definition"
                      value={criterion.score10_definition}
                      rows={1}
                      onChange={(e) =>
                        handleCriteriaChange(
                          criterion.id,
                          "score10_definition",
                          e.target.value
                        )
                      }
                    />
                  </div>
                ))}

                <div className="criteria-editor-actions">
                  <button
                    className="add-criteria-button app-button app-button--secondary"
                    onClick={addCriteria}
                  >
                    Add Criteria
                  </button>
                  <button
                    className="generate-criteria-button menu-style-button app-button app-button--primary"
                    onClick={() => saveCriteriaUpdates()}
                    disabled={isSavingCriteria || isAutoSavingCriteria}
                  >
                    {isSavingCriteria ? "SAVING..." : isAutoSavingCriteria ? "AUTO-SAVING..." : "SAVE CRITERIA"}
                  </button>
                </div>
                {(isSavingCriteria || isAutoSavingCriteria || criteriaSaveMessage) && (
                  <p
                    className={`help-text status-text${
                      isSavingCriteria || isAutoSavingCriteria ? " status-text--active" : ""
                    }`}
                  >
                    {isSavingCriteria
                      ? "Saving criteria..."
                      : isAutoSavingCriteria
                        ? "Saving changes automatically..."
                        : criteriaSaveMessage}
                  </p>
                )}
              </section>
            ) : !showAnalytics && !isProfileSidebarOpen ? (
              <button
                type="button"
                className="criteria-panel-open app-button app-button--secondary"
                onClick={() => { setShowCriteriaEditor(true); setShowAnalytics(false); setIsProfileSidebarOpen(false); }}
              >
                Open Criteria <FaChevronDown className="arrow" />
              </button>
            ) : null}
          </aside>
        </div>
      )}
    </div>
  );
};

export default JobList;